import {
  DashboardDefinition,
  definitionsDifferInLayout,
  normalizeDefinition,
} from "../DashboardHeader/DashboardParams";

/**
 * What to do with a localStorage draft when the dashboard seeds a context
 * (org / scope / dashboard id):
 *
 * - `"seed-baseline"` — no draft exists; seed the baseline (saved record or
 *   default layout) silently, exactly the draft-less behavior.
 * - `"drop-draft"` — a draft exists but holds no real LAYOUT edit: it
 *   normalizes to the baseline's sections + cards (a params-only `range`/
 *   `period` drift counts as no edit — the URL carries the user's params,
 *   so prompting over them would be the same class of interruption the
 *   leave guard avoids). Clear it and seed the baseline silently.
 * - `"prompt"` — the draft differs from the baseline in layout; seed the
 *   baseline beneath a dialog and let the user pick the draft or the
 *   baseline. Restoring applies the draft's params too — they rode along
 *   with the layout edit.
 */
export type DraftRestorePlan =
  | { kind: "seed-baseline" }
  | { kind: "drop-draft" }
  | { kind: "prompt"; draft: DashboardDefinition };

/**
 * Decide how a stored draft should participate in seeding, given the
 * baseline that would load without it (the saved record's definition, or
 * the default layout for an unsaved view). The decision compares LAYOUT
 * only ({@link definitionsDifferInLayout}): a draft or record compares on
 * meaning, not stored bytes, and a params-only difference never prompts.
 */
export function planDraftRestore(
  draft: DashboardDefinition | null,
  baseline: DashboardDefinition,
): DraftRestorePlan {
  if (draft === null) {
    return { kind: "seed-baseline" };
  }
  const normalizedDraft = normalizeDefinition(draft);
  if (!definitionsDifferInLayout(normalizedDraft, baseline)) {
    return { kind: "drop-draft" };
  }
  return { kind: "prompt", draft: normalizedDraft };
}
